import React, { useCallback, useEffect, useRef, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import {
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@material-ui/core";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import { StatusBadge } from "../../components/ui";
import { usePanelStyles, FormField } from "../../components/QueueIntegrationModal/panelKit";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { getTokens } from "../../styles/tokens";

import { ROWS_PER_PAGE, formatCentavos } from "./helpers";

const COLUMNS = 6;

const STATUS_OPTIONS = ["pago", "pendente", "expirado"];

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    filters: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 220px))",
      gap: 12,
      marginBottom: 14,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
    },
    table: { minWidth: 820 },
    pagination: { borderTop: `1px solid ${t.line}` },
  };
});

const statusTone = (status) => {
  const key = String(status || "").toLowerCase();
  if (key === "pago") return "success";
  if (key === "expirado") return "error";
  return "warning";
};

const statusLabel = (status) => {
  const key = String(status || "").toLowerCase();
  if (STATUS_OPTIONS.includes(key)) return i18n.t(`hotspot.payments.status.${key}`);
  return status || "-";
};

// Payment field names are not guaranteed by the hotspot API — read defensively.
const paymentDate = (p) => p.data || p.criadoEm || p.createdAt || p.dataPagamento || p.pagoEm;
const paymentDescription = (p) =>
  p.descricao || p.pacote || p.lote?.nome || p.loteNome || p.produto || "-";
const paymentValueCents = (p) => {
  const v = p.valorCentavos ?? p.valor ?? p.amount ?? p.valorTotal;
  return v === undefined || v === null ? null : v;
};
const paymentGateway = (p) => p.gateway || p.metodo || p.paymentMethod || p.provider || "-";
const paymentDeviceName = (p, devicesById) => {
  if (p.device && p.device.nome) return p.device.nome;
  if (p.deviceNome) return p.deviceNome;
  if (p.equipamento) return p.equipamento;
  if (p.deviceId && devicesById[p.deviceId]) return devicesById[p.deviceId];
  return null;
};

const PaymentsTab = () => {
  const classes = useStyles();
  const panel = usePanelStyles();
  const { datetimeToClient } = useDate();

  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);

  const [status, setStatus] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [page, setPage] = useState(0);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/hotspot/devices");
        if (active) setDevices(Array.isArray(data) ? data : []);
      } catch (err) {
        // silent: the device filter just falls back to "all"
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const devicesById = devices.reduce((acc, d) => {
    if (d.id) acc[d.id] = d.nome;
    return acc;
  }, {});

  const fetchPayments = useCallback(async (filters, pageIndex) => {
    setLoading(true);
    try {
      const { data } = await api.get("/hotspot/payments", {
        params: {
          status: filters.status || undefined,
          deviceId: filters.deviceId || undefined,
          de: filters.de || undefined,
          ate: filters.ate || undefined,
          take: ROWS_PER_PAGE,
          skip: pageIndex * ROWS_PER_PAGE,
        },
      });
      if (!isMountedRef.current) return;
      if (Array.isArray(data)) {
        setPayments(data);
        setTotal(data.length);
      } else {
        const list = Array.isArray(data?.payments)
          ? data.payments
          : Array.isArray(data?.items)
          ? data.items
          : [];
        setPayments(list);
        setTotal(Number(data?.total) || list.length);
      }
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayments({ status, deviceId, de, ate }, page);
  }, [fetchPayments, status, deviceId, de, ate, page]);

  const handleFilterChange = (setter) => (e) => {
    setPage(0);
    setter(e.target.value);
  };

  return (
    <>
      <div className={classes.filters}>
        <FormField label={i18n.t("hotspot.payments.filters.status")}>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            value={status}
            onChange={handleFilterChange(setStatus)}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">{i18n.t("hotspot.payments.filters.allStatuses")}</MenuItem>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {i18n.t(`hotspot.payments.status.${opt}`)}
              </MenuItem>
            ))}
          </TextField>
        </FormField>
        <FormField label={i18n.t("hotspot.payments.filters.device")}>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            value={deviceId}
            onChange={handleFilterChange(setDeviceId)}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">{i18n.t("hotspot.payments.filters.allDevices")}</MenuItem>
            {devices.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.nome}
              </MenuItem>
            ))}
          </TextField>
        </FormField>
        <FormField label={i18n.t("hotspot.payments.filters.from")} htmlFor="hotspot-payments-de">
          <TextField
            id="hotspot-payments-de"
            fullWidth
            size="small"
            variant="outlined"
            type="date"
            value={de}
            onChange={handleFilterChange(setDe)}
          />
        </FormField>
        <FormField label={i18n.t("hotspot.payments.filters.to")} htmlFor="hotspot-payments-ate">
          <TextField
            id="hotspot-payments-ate"
            fullWidth
            size="small"
            variant="outlined"
            type="date"
            value={ate}
            onChange={handleFilterChange(setAte)}
          />
        </FormField>
      </div>

      <Paper className={`${panel.tableCard} ${panel.tableCardAuto}`} elevation={0}>
        {!loading && payments.length === 0 ? (
          <div className={panel.empty}>{i18n.t("hotspot.payments.table.empty")}</div>
        ) : (
          <>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("hotspot.payments.table.date")}</TableCell>
                  <TableCell>{i18n.t("hotspot.payments.table.device")}</TableCell>
                  <TableCell>{i18n.t("hotspot.payments.table.description")}</TableCell>
                  <TableCell align="right">{i18n.t("hotspot.payments.table.value")}</TableCell>
                  <TableCell>{i18n.t("hotspot.payments.table.status")}</TableCell>
                  <TableCell>{i18n.t("hotspot.payments.table.gateway")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p, idx) => {
                  const dt = paymentDate(p);
                  const deviceName = paymentDeviceName(p, devicesById);
                  const cents = paymentValueCents(p);
                  return (
                    <TableRow key={p.id || idx}>
                      <TableCell>{dt ? datetimeToClient(dt) : "-"}</TableCell>
                      <TableCell>{deviceName || i18n.t("hotspot.payments.table.noDevice")}</TableCell>
                      <TableCell>{paymentDescription(p)}</TableCell>
                      <TableCell align="right" className={panel.cellStrong}>
                        {cents !== null ? formatCentavos(cents) : "-"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge color={statusTone(p.status)} label={statusLabel(p.status)} />
                      </TableCell>
                      <TableCell>{paymentGateway(p)}</TableCell>
                    </TableRow>
                  );
                })}
                {loading && <TableRowSkeleton columns={COLUMNS} />}
              </TableBody>
            </Table>
            {total > ROWS_PER_PAGE && (
              <TablePagination
                className={classes.pagination}
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[]}
              />
            )}
          </>
        )}
      </Paper>
    </>
  );
};

export default PaymentsTab;
